import { Box } from "@mui/material";
import { createPortal } from "react-dom";

import { useDispatchWindows, useWindows, windowsActions } from "contexts/windows";
import { ELEMENT_IDS } from "features/bookmarks/utils";
import { useNonMarkerUIIsHidden } from "features/markers/markerCreationUtils";

import { VIEWER_INSET } from "./window";

/**
 * Renders all open windows from Redux state.
 *
 * Core windows are registered by CoreWindowsProvider.
 * Dynamic windows can be added via windowsActions.addWindow.
 * All windows use BaseWindowProps interface.
 */
export function WindowManager() {
    const windows = useWindows();
    const dispatchWindows = useDispatchWindows();
    const nonMarkerUIIsHidden = useNonMarkerUIIsHidden();

    const handleCloseWindow = (windowId: string) => {
        dispatchWindows(windowsActions.closeWindow(windowId));
    };

    const handleFocusWindow = (windowId: string) => {
        const currentWindow = windows[windowId];
        if (!currentWindow) {
            return;
        }

        const maxZ = Math.max(0, ...Object.values(windows).map((w) => w.state.zIndex || 0));

        if ((currentWindow.state.zIndex || 0) >= maxZ) {
            return;
        }

        dispatchWindows(windowsActions.focusWindow(windowId));
    };

    const container = document.getElementById(ELEMENT_IDS.renderContainer);
    if (!container) {
        return null;
    }

    return createPortal(
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                visibility: nonMarkerUIIsHidden ? "hidden" : undefined,
                pointerEvents: "none",
            }}
        >
            {Object.entries(windows).map(([windowId, windowEntry]) => {
                const { config, state } = windowEntry;

                if (!state.open) {
                    return null;
                }

                const WindowComponent = config.component;

                // `position: absolute` inset-0 fills the same coordinate space as
                // render-container, so Rnd's `bounds="parent"` correctly constrains
                // dragging to the viewer area.
                // `zIndex` on this wrapper controls cross-window stacking order.
                return (
                    <div
                        key={windowId}
                        style={{
                            position: "absolute",
                            inset: VIEWER_INSET,
                            zIndex: state.zIndex,
                            pointerEvents: "none",
                        }}
                        onPointerDownCapture={() => handleFocusWindow(windowId)}
                    >
                        <WindowComponent position={state.position} onClose={() => handleCloseWindow(windowId)} />
                    </div>
                );
            })}
        </Box>,
        container,
    );
}
